// Explorer Action Planning Service - Control generation for Explorer assessments
// Uses Explorer-specific Redux state (explorerRisks, not modelRisks)
import {
  saveControl,
  updateControlEvaluationField
} from '../../redux/actions/controlActions';
import { PolicyMatchingService } from '../../autopilotMode/services/PolicyMatchingService';
import { authFetch } from '../../utils/authFetch';

export class ExplorerActionPlanningService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeActionPlanning(areaKey, areaDefinition, contextData, templateConfig, inheritedRisks, progressCallback) {
    try {
      console.log(`[ExplorerActionPlanningService] Starting action planning for area: ${areaKey}`);
      if (progressCallback) progressCallback('actions');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Access Explorer risks (not modelRisks)
      const explorerRisks = currentState.riskItems?.byAssessment?.[this.assessmentUuid]?.explorerRisks || {};
      const areaRisks = explorerRisks[areaKey]?.areaRisks || {};

      const evaluatedRisks = [];

      console.log(`[ExplorerActionPlanningService] State source for area: ${areaKey}`);
      console.log(`[ExplorerActionPlanningService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[ExplorerActionPlanningService] Available risk categories:`, Object.keys(areaRisks));

      Object.entries(areaRisks).forEach(([category, riskArray]) => {
        console.log(`[ExplorerActionPlanningService] Checking category "${category}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[ExplorerActionPlanningService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && risk.priority) {
              console.log(`[ExplorerActionPlanningService] FOUND evaluated risk for action planning: "${risk.riskName}"`);
              evaluatedRisks.push({
                ...risk,
                areaKey,
                category
              });
            }
          });
        }
      });

      let actionsGenerated = 0;

      // Get template configuration for RAG
      const usePolicyRAG = templateConfig?.use_control_rag === true;
      const policyTypes = templateConfig?.policy_types || ['governance', 'compliance', 'operational'];
      const riskNarrative = contextData?.riskNarrative || '';

      console.log('[ExplorerActionPlanningService] Template RAG config:', {
        use_control_rag: usePolicyRAG,
        policy_types: policyTypes,
        has_narrative: !!riskNarrative
      });

      // Generate and evaluate actions per risk
      for (const risk of evaluatedRisks) {
        try {
          // STEP 1: Generate controls via Explorer-specific AI endpoint
          console.log(`[ExplorerActionPlanningService] Generating controls via AI for risk: ${risk.riskName}`);

          const controlsResponse = await authFetch('/.netlify/functions/explorerGenerateControls', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              // Function expects 'risk' object with riskName inside
              risk: {
                riskId: risk.riskId,
                riskName: risk.riskName,
                areaName: areaKey,
                areaKey: areaKey,
                priority: risk.priority,
                businessImpact: risk.businessImpact,
                stakeholders: risk.stakeholders,
                category: risk.category
              },
              riskNarrative: contextData?.riskNarrative || '',
              businessContext: contextData?.businessContext || '',
              parentAssessmentUuid: contextData?.parentAssessmentUuid,
              // Only use Policy RAG if EXPLICITLY enabled in template config
              usePolicyRAG: templateConfig?.use_control_rag === true,
              policyTypes: templateConfig?.policy_types || ['governance', 'ai-control']
            })
          });

          if (!controlsResponse.ok) {
            console.error(`[ExplorerActionPlanningService] AI generation failed for ${risk.riskName}`);
            continue;
          }

          // Response is { controls: [...], method: ..., policiesUsed: ... }
          const responseData = await controlsResponse.json();
          let controls = responseData.controls || [];

          console.log(`[ExplorerActionPlanningService] Received ${controls.length} controls for ${risk.riskName}`);

          // STEP 2: If policy matching enabled, enrich controls with policy evidence
          if (usePolicyRAG && controls.length > 0) {
            console.log(`[ExplorerActionPlanningService] Matching ${controls.length} controls with policy directives for risk: ${risk.riskName}`);

            try {
              const policyService = new PolicyMatchingService();
              controls = await policyService.enrichControlsWithPolicies(controls, {
                policyTypes: policyTypes,
                riskNarrative: riskNarrative,
                threshold: 0.3,
                topK: 3
              });

              const matchedCount = controls.filter(c => c.policyEvidence?.length > 0).length;
              console.log(`[ExplorerActionPlanningService] ${matchedCount}/${controls.length} controls matched with policy directives`);

              // LIVE UPDATE: Stream policy matching progress
              if (this.liveUpdateCallback) {
                this.liveUpdateCallback('policyMatching', {
                  riskName: risk.riskName,
                  totalControls: controls.length,
                  matchedControls: matchedCount,
                  areaKey
                });
              }
            } catch (policyError) {
              console.error(`[ExplorerActionPlanningService] Policy matching error for ${risk.riskName}:`, policyError.message);
            }
          }

          if (controls.length > 0) {
            // Auto-select top 3 controls and save them
            const topControls = controls.slice(0, 3);

            // LIVE UPDATE: Stream control generation
            if (this.liveUpdateCallback) {
              this.liveUpdateCallback('controlsGenerated', {
                riskName: risk.riskName,
                controlsCount: topControls.length,
                actionsGenerated,
                areaKey,
                controls: topControls.map(c => ({
                  controlId: c.controlId,
                  name: c.name || c.title,
                  description: c.description,
                  policyEvidence: c.policyEvidence || [],
                  source: c.source || 'AI_Generated'
                }))
              });
            }

            for (const control of topControls) {
              // Save control using existing Redux action
              this.dispatch(saveControl({
                risk: {
                  ...risk,
                  sourceName: risk.category, // Map category to sourceName for compatibility
                  areaName: risk.areaKey // Map areaKey to areaName for compatibility
                },
                control: {
                  ...control,
                  policyEvidence: control.policyEvidence || [],
                  source: control.source || 'AI_Generated',
                  assessmentType: 'explorer' // Mark as Explorer control
                },
                modelId: this.modelId,
                assessmentUuid: this.assessmentUuid,
                controlModelType: 'advanced',
                controlModelStage: 'production'
              }));

              // Generate evaluation for the control
              const controlEvalCfg = areaDefinition?.controlEvaluationForm || [];
              if (controlEvalCfg.length > 0) {
                const evalResponse = await authFetch('/.netlify/functions/generateControlEvaluationData', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    risk,
                    control,
                    evaluationConfig: controlEvalCfg
                  })
                });

                if (evalResponse.ok) {
                  const evalData = await evalResponse.json();
                  if (evalData.success && evalData.evaluationData) {
                    this.dispatch(updateControlEvaluationField(
                      risk.riskId,
                      control.controlId,
                      'generated_evaluation',
                      evalData.evaluationData
                    ));

                    // LIVE UPDATE: Stream control evaluation
                    if (this.liveUpdateCallback) {
                      this.liveUpdateCallback('controlEvaluated', {
                        riskName: risk.riskName,
                        controlName: control.name || control.title,
                        evaluationData: evalData.evaluationData,
                        areaKey
                      });
                    }
                  }
                }
              }

              actionsGenerated++;
            }
          }
        } catch (actionError) {
          console.error(`[ExplorerActionPlanningService] Action planning failed for ${risk.riskName}:`, actionError);
        }
      }

      return {
        evaluatedRisks: evaluatedRisks.length,
        actionsGenerated,
        areaKey
      };

    } catch (error) {
      console.error('[ExplorerActionPlanningService] Action planning failed:', error);
      throw error;
    }
  }
}
